import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PURCHASING_TYPES, CLOSE_TIMES, MARKET_OPTIONS } from "@/constants/buyerFormOptions";

interface FormData {
  preferredMarkets: string[];
  purchasingType: string[];
  averageCloseTime: string;
}

interface BuyerInvestmentPreferencesSectionProps {
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
}

const BuyerInvestmentPreferencesSection = ({ formData, updateFormData }: BuyerInvestmentPreferencesSectionProps) => {
  const handleMarketToggle = (market: string, checked: boolean) => {
    const newMarkets = checked 
      ? [...formData.preferredMarkets, market]
      : formData.preferredMarkets.filter(m => m !== market);
    updateFormData({ preferredMarkets: newMarkets });
  };

  const handlePurchasingTypeToggle = (type: string, checked: boolean) => {
    const newTypes = checked 
      ? [...formData.purchasingType, type]
      : formData.purchasingType.filter(t => t !== type);
    updateFormData({ purchasingType: newTypes });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-[#ffd700] mb-4">Investment Preferences</h3>
      
      {/* Purchasing Types */}
      <div>
        <Label className="text-white text-base font-medium">
          Purchasing Types (Select at least one)
        </Label>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3 p-4 bg-gray-700 rounded border border-gray-600">
          {PURCHASING_TYPES.map((type) => (
            <div key={type} className="flex items-center space-x-2">
              <Checkbox
                id={`purchasing-${type}`}
                checked={formData.purchasingType.includes(type)}
                onCheckedChange={(checked) => handlePurchasingTypeToggle(type, checked as boolean)}
                className="border-gray-400"
              />
              <Label 
                htmlFor={`purchasing-${type}`} 
                className="text-sm text-white cursor-pointer"
              >
                {type}
              </Label>
            </div>
          ))}
        </div>
        {formData.purchasingType.length === 0 && (
          <p className="text-sm text-red-400 mt-1">Please select at least one purchasing type</p>
        )}
      </div>

      {/* Average Close Time */}
      <div>
        <Label htmlFor="averageCloseTime" className="text-white text-base font-medium">
          Average Close Time
        </Label>
        <Select 
          value={formData.averageCloseTime}
          onValueChange={(value) => updateFormData({ averageCloseTime: value })}
        >
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white mt-2">
            <SelectValue placeholder="Select close time" />
          </SelectTrigger>
          <SelectContent className="bg-gray-700 border-gray-600">
            {CLOSE_TIMES.map((time) => (
              <SelectItem key={time} value={time} className="text-white">
                {time}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!formData.averageCloseTime && (
          <p className="text-sm text-red-400 mt-1">Please select your average close time</p>
        )}
      </div>

      {/* Preferred Markets */}
      <div>
        <Label className="text-white text-base font-medium">
          Preferred Markets (Select at least one)
        </Label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-4 bg-gray-700 rounded border border-gray-600 mt-3">
          {MARKET_OPTIONS.map((market) => (
            <div key={market} className="flex items-center space-x-2">
              <Checkbox
                id={`market-${market}`}
                checked={formData.preferredMarkets.includes(market)}
                onCheckedChange={(checked) => handleMarketToggle(market, checked as boolean)}
                className="border-gray-400"
              />
              <Label 
                htmlFor={`market-${market}`} 
                className="text-sm text-white cursor-pointer"
              >
                {market}
              </Label>
            </div>
          ))}
        </div>
        {formData.preferredMarkets.length === 0 && (
          <p className="text-sm text-red-400 mt-1">Please select at least one preferred market</p>
        )}
      </div>
    </div>
  );
};

export default BuyerInvestmentPreferencesSection;